import { useMemo } from 'react'
import DOMPurify from 'dompurify'
import { cn } from '@/lib/cn'

/**
 * Prose-like styling shared by the editor surface ({@link RichTextEditor}) and the rendered output
 * here, so a note looks the same while it is being typed and after it is saved. No typography
 * plugin — just Tailwind arbitrary-variant selectors. {@code whitespace-pre-wrap} preserves the
 * line breaks of legacy plain-text notes (which have no <p>/<br> markup at all).
 */
export const richTextClass =
  'text-sm text-brand-dark whitespace-pre-wrap break-words ' +
  '[&_p]:my-0.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 ' +
  '[&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold ' +
  '[&_a]:text-brand-primary [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-gray-200 [&_blockquote]:pl-3 [&_blockquote]:text-gray-500 ' +
  // Cap any embedded image (e.g. a signature avatar) so it never blows out the timeline width.
  '[&_img]:max-w-[96px] [&_img]:h-auto [&_img]:rounded'

/**
 * Allowlist mirrors the backend {@code HtmlSanitizer} — defense in depth on the render side.
 * {@code target} is deliberately absent (as on the backend): rendered links never carry a bare
 * {@code target="_blank"}, which would enable reverse tabnabbing without {@code rel=noopener}.
 */
const ALLOWED_TAGS = ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'blockquote', 'a', 'img']
const ALLOWED_ATTR = ['href', 'rel', 'src', 'alt', 'width', 'height']

/**
 * Renders stored HTML (a note or message body) safely: the body is always run through
 * DOMPurify before it reaches {@code dangerouslySetInnerHTML} — raw HTML is never rendered.
 * Legacy plain-text bodies pass through unchanged and keep their line breaks via pre-wrap.
 */
/**
 * An email as sent can carry a signature pasted from Gmail / Outlook — a table layout with inline
 * styles. The "email" variant keeps that layout (mirroring the backend's signature allowlist); still
 * nothing executable (DOMPurify drops scripts, handlers and javascript: URLs whatever the allowlist).
 */
const EMAIL_TAGS = [...ALLOWED_TAGS, 'span', 'div', 'font', 'hr', 'small', 'sub', 'sup', 'table', 'tbody', 'thead', 'tr', 'td', 'th']
const EMAIL_ATTR = [...ALLOWED_ATTR, 'style', 'align', 'valign', 'bgcolor', 'colspan', 'rowspan', 'cellpadding', 'cellspacing', 'border', 'color', 'face', 'size']

export default function RichText({
  html,
  className,
  variant = 'note',
}: {
  html: string | null | undefined
  className?: string
  /** "email" keeps a pasted signature's table layout and styles (previews of what's sent). */
  variant?: 'note' | 'email'
}) {
  const clean = useMemo(
    () =>
      variant === 'email'
        ? DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS: EMAIL_TAGS, ALLOWED_ATTR: EMAIL_ATTR })
        : DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS, ALLOWED_ATTR }),
    [html, variant],
  )
  return (
    <div
      className={cn(richTextClass, variant === 'email' && '[&_img]:max-w-[240px] [&_table]:border-collapse', className)}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  )
}
